import type { z } from "zod";
import type { cadenceTierSchema, healthStateSchema, scanStateSchema } from "./sources.js";
import { HOUR_MS, DAY_MS, plural } from "@amber/contracts";

type HealthState = z.infer<typeof healthStateSchema>;
type CadenceTier = z.infer<typeof cadenceTierSchema>;

export interface HealthRules {
  readonly lateMultiplier: number;
  readonly quietMultiplier: number;
  readonly quietMinDays: number;
  readonly quietWindow: number;
  readonly quietMinPublications: number;
  readonly cadenceHours: Readonly<Record<CadenceTier, number>>;
}

export const STARTING_HEALTH_RULES: HealthRules = {
  lateMultiplier: 2,
  quietMultiplier: 3,
  quietMinDays: 7,
  quietWindow: 10,
  quietMinPublications: 5,
  cadenceHours: { priority: 2, standard: 12, low: 24 },
};

export interface HealthBreak {
  readonly from: string;
  readonly to: string | null;
  readonly endedAt: string | null;
}

export interface HealthInput {
  readonly now: Date;
  readonly tier: CadenceTier;
  readonly monitoredSince: Date;
  readonly lastScan: { readonly state: z.infer<typeof scanStateSchema>; readonly createdAt: Date } | null;
  readonly lastFinishedScan: {
    readonly state: "complete" | "partial" | "failed";
    readonly stopReason: string | null;
  } | null;
  readonly publications: readonly Date[];
  readonly breaks: readonly HealthBreak[];
  readonly quietLatchedAt: Date | null;
}

export interface HealthResult {
  readonly state: HealthState;
  readonly reason: string | null;
  readonly usualGapDays: number | null;
  readonly silentDays: number | null;
  readonly quietLatch: Date | null;
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] ?? null) : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

export function usualGapMs(publications: readonly Date[], window: number): number | null {
  const recent = [...publications].sort((a, b) => b.getTime() - a.getTime()).slice(0, window);
  const gaps: number[] = [];
  for (let i = 1; i < recent.length; i += 1) gaps.push((recent[i - 1]?.getTime() ?? 0) - (recent[i]?.getTime() ?? 0));
  return median(gaps);
}

export function activeBreak(breaks: readonly HealthBreak[], now: Date): HealthBreak | null {
  const today = now.toISOString().slice(0, 10);
  return breaks.find((b) => b.endedAt === null && b.from <= today && (b.to === null || b.to >= today)) ?? null;
}

export function cadenceMs(rules: HealthRules, tier: CadenceTier): number {
  return rules.cadenceHours[tier] * HOUR_MS;
}

export function formatGap(days: number): string {
  if (days < 1) return plural(Math.max(1, Math.round(days * 24)), "hour");
  if (days >= 13.5 && days % 7 < 1) return plural(Math.round(days / 7), "week");
  return plural(Math.round(days), "day");
}

function scanProblem(input: HealthInput, rules: HealthRules): HealthResult | null {
  const none = { usualGapDays: null, silentDays: null, quietLatch: input.quietLatchedAt };
  const finished = input.lastFinishedScan;
  if (finished?.state === "failed")
    return {
      state: "failing",
      reason: finished.stopReason ?? "The latest scan failed after its automatic attempts",
      ...none,
    };
  if (finished?.state === "partial")
    return {
      state: "partial",
      reason: finished.stopReason ?? "The latest scan stopped before the end of the listing",
      ...none,
    };
  const cadence = cadenceMs(rules, input.tier);
  const lastRun = input.lastScan?.createdAt ?? input.monitoredSince;
  if (input.now.getTime() - lastRun.getTime() <= rules.lateMultiplier * cadence) return null;
  return {
    state: "late",
    reason: input.lastScan
      ? `No scan since ${formatGap((input.now.getTime() - lastRun.getTime()) / DAY_MS)}; expected every ${formatGap(cadence / DAY_MS)}`
      : "The first scan has not run yet",
    ...none,
  };
}

export function sourceHealth(input: HealthInput, rules: HealthRules = STARTING_HEALTH_RULES): HealthResult {
  const problem = scanProblem(input, rules);
  if (problem) return problem;
  const pubs = [...input.publications].sort((a, b) => b.getTime() - a.getTime());
  const newest = pubs[0];
  const gap = usualGapMs(pubs, rules.quietWindow);
  const usualGapDays = gap === null ? null : gap / DAY_MS;
  const silentDays = newest ? (input.now.getTime() - newest.getTime()) / DAY_MS : null;
  const suppressed = activeBreak(input.breaks, input.now) !== null;
  const latchHolds = input.quietLatchedAt !== null && (!newest || newest.getTime() <= input.quietLatchedAt.getTime());
  const enough = pubs.length >= rules.quietMinPublications;
  const condition =
    enough &&
    gap !== null &&
    newest !== undefined &&
    input.now.getTime() - newest.getTime() >= Math.max(rules.quietMultiplier * gap, rules.quietMinDays * DAY_MS);
  if (!suppressed && enough && (condition || latchHolds)) {
    return {
      state: "unusually_quiet",
      reason: `No new items for ${formatGap(silentDays ?? 0)}; usually every ${formatGap(usualGapDays ?? 0)}`,
      usualGapDays,
      silentDays,
      quietLatch: input.quietLatchedAt ?? input.now,
    };
  }
  return { state: "healthy", reason: null, usualGapDays, silentDays, quietLatch: null };
}

export const HEALTH_LABELS: Record<HealthState | "paused" | "not_in_any_plan" | "not_collected", string> = {
  failing: "Failing",
  partial: "Partial",
  late: "Late",
  unusually_quiet: "Unusually quiet",
  healthy: "Healthy",
  paused: "Paused",
  not_in_any_plan: "Not in any plan",
  not_collected: "Not collected yet",
};

export const HEALTH_ORDER = [
  "failing",
  "partial",
  "late",
  "unusually_quiet",
  "healthy",
  "paused",
  "not_in_any_plan",
  "not_collected",
] as const;

export function delivering(state: (typeof HEALTH_ORDER)[number]): boolean | null {
  if (state === "paused" || state === "not_in_any_plan" || state === "not_collected") return null;
  return state === "healthy" || state === "late";
}
